// src/pages/ContactsPage.tsx
"use client";

import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Checkbox,
  Alert,
  Divider,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  Group as GroupIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import axios from "axios";
import { useSelector } from "react-redux";
import { RootState } from "../redux/store";

interface Friend {
  friendName: string;
  fmsisdn: string;
  isBlacklisted: boolean;
}

interface Group {
  groupId: string;
  groupName: string;
  friendList: Friend[];
}

const ContactsPage: React.FC = () => {
  const [friends, setFriends] = useState<Friend[]>([]);

  const [groups, setGroups] = useState<Group[]>([]);

  const [openFriendDialog, setOpenFriendDialog] = useState(false);
  const [openGroupDialog, setOpenGroupDialog] = useState(false);
  const [editFriend, setEditFriend] = useState<Friend | null>(null);
  const [editGroup, setEditGroup] = useState<Group | null>(null);
  const [newFriend, setNewFriend] = useState<Friend>({
    friendName: "",
    fmsisdn: "",
    isBlacklisted: false,
  });
  const [countryList, setCountryList] = useState<any[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<string>("");
  const [newGroup, setNewGroup] = useState<string>("");
  const [openAddFriendToGroupDialog, setOpenAddFriendToGroupDialog] =
    useState(false);
  const [selectedGroupForFriends, setSelectedGroupForFriends] =
    useState<Group | null>(null);

  const [selectedFriends, setSelectedFriends] = useState<string[]>([]);

  const { countryCode, msisdn } = useSelector(
    (state: RootState) => state.phone
  );

  const { subType } = useSelector((state: RootState) => state.profile);

  const prefixLength = selectedCountry.length;

  const handleAddFriend = async () => {
    try {
      const username = countryCode + msisdn;
      const fmsisdn = newFriend.fmsisdn;
      const nickName = newFriend.friendName;

      const url = `${process.env.REACT_APP_API_URL}/Friend/AddFriend?msisdn=${username}&service=${process.env.REACT_APP_SERVICE}&interface=${process.env.REACT_APP_INTERFACES}&langId=${process.env.REACT_APP_LANGID}&fmsisdn=${fmsisdn}&friendName=${nickName}&subType=${subType}`;

      const res = await axios.post(url);

      if (res.data.resCode === 209) {
        alert(res.data.resMessage);
      } else {
        alert(res.data.resMessage);
      }

      await getAllFriends();
      setNewFriend({ friendName: "", fmsisdn: "", isBlacklisted: false });
      setOpenFriendDialog(false);
    } catch (err) {
      console.error("Error adding friend:", err);
      alert("Failed to add friend.");
    }
  };

  const handleEditFriend = async () => {
    try {
      const username = countryCode + msisdn;
      const fmsisdn = editFriend?.fmsisdn || "";
      const frndName = editFriend?.friendName || "";

      const url = `${process.env.REACT_APP_API_URL}/Friend/RenameFriend?msisdn=${username}&service=${process.env.REACT_APP_SERVICE}&interface=${process.env.REACT_APP_INTERFACES}&langId=${process.env.REACT_APP_LANGID}&fmsisdn=${fmsisdn}&friendName=${frndName}&subType=${subType}`;

      const res = await axios.put(url);

      if (res.data.resCode === 211) {
        alert(res.data.resMessage);
      } else {
        alert(res.data.resMessage);
      }

      await getAllFriends();
      setEditFriend(null);
    } catch (err) {
      console.error("Error renaming friend:", err);
      alert("Failed to rename friend.");
    }
  };

  const handleDeleteFriend = async (fmsisdn: string) => {
    const username = countryCode + msisdn;

    if (window.confirm("Are you sure you want to delete this friend?")) {
      try {
        const url = `${process.env.REACT_APP_API_URL}/Friend/DeleteFriend?msisdn=${username}&service=${process.env.REACT_APP_SERVICE}&interface=${process.env.REACT_APP_INTERFACES}&langId=${process.env.REACT_APP_LANGID}&fmsisdn=${fmsisdn}&subType=${subType}`;
        const res = await axios.delete(url);

        if(res.data.resCode === 215){
          await getAllFriends();
          alert(res?.data?.resMessage)
        }else {
          alert(res?.data?.resMessage)
        }

      } catch (err) {
        console.error("Error deleting friend:", err);
        alert("Failed to delete friend.");
      }
    }
  };

  const handleRemoveFriendFromGroup = async (
    groupId: string,
    fMsisdn: string,
    friendName: string,
    groupName: string
  ) => {
    if (!window.confirm(`Remove ${friendName} from group "${groupName}"?`))
      return;

    try {
      const username = countryCode + msisdn;

      const url = `${process.env.REACT_APP_API_URL}/Group/deleteFriendFromGroup?msisdn=${username}&service=${process.env.REACT_APP_SERVICE}&interface=${process.env.REACT_APP_INTERFACES}&langId=${process.env.REACT_APP_LANGID}&groupId=${groupId}&subType=${subType}&fMsisdn=${fMsisdn}`;

      const res = await axios.delete(url);

      if (res?.data?.resCode === 157) {
        alert(res.data.resMessage);
      } else {
        alert(res.data.resMessage);
      }

      await showAllGroups();
    } catch (err) {
      console.error("Error removing friend from group:", err);
      alert("Failed to remove friend.");
    }
  };

  const handleAddGroup = async () => {
    try {
      const username = countryCode + msisdn;

      const body = {
        msisdn: username,
        service: process.env.REACT_APP_SERVICE,
        interfaceUsed: process.env.REACT_APP_INTERFACES,
        langId: process.env.REACT_APP_LANGID,
        groupName: newGroup,
        groupMembers: "",
        subType: subType,
      };

      const url = `${process.env.REACT_APP_API_URL}/Group/AddGroup`;

      const res = await axios.post(url, body, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (res.data.resCode === -257) {
        await showAllGroups();
        setNewGroup("");
        setOpenGroupDialog(false);
        alert(res?.data?.resMessage);
      } else {
        alert(res?.data?.resMessage);
      }
    } catch (error) {
      console.error("Error adding group:", error);
      alert("Failed to create group.");
    }
  };

  const handleAddFriendToGroup = async () => {
    try {
      const username = countryCode + msisdn;
      const groupId = selectedGroupForFriends?.groupId || "";
      const groupName = selectedGroupForFriends?.groupName || "";
      const groupMembers = selectedFriends.join(",");

      const url = `${process.env.REACT_APP_API_URL}/Group/addGroupMembers`;

      const body = {
        msisdn: username,
        service: process.env.REACT_APP_SERVICE,
        interfaceUsed: process.env.REACT_APP_INTERFACES,
        langId: process.env.REACT_APP_LANGID,
        groupId: groupId,
        groupMembers: groupMembers,
        subType: subType,
        groupName: groupName,
      };

      const res = await axios.post(url, body, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (res?.data?.resCode === 155) {
        alert(`${res?.data?.resMessage}`);
      } else {
        alert(`${res?.data?.resMessage || "Failed to add friends."}`);
      }

      await showAllGroups();
      setOpenAddFriendToGroupDialog(false);
    } catch (error) {
      console.error("Error adding friend to group:", error);
      alert("Failed to add friend to group.");
    }
  };

  const handleEditGroup = async () => {
    try {
      const username = countryCode + msisdn;

      const payload = {
        msisdn: username,
        service: process.env.REACT_APP_SERVICE,
        interfaceUsed: process.env.REACT_APP_INTERFACES,
        langId: process.env.REACT_APP_LANGID,
        groupId: editGroup?.groupId || "",
        newGroupName: editGroup?.groupName || "",
        groupName: editGroup?.groupName,
        subType: subType,
      };

      const url = `${process.env.REACT_APP_API_URL}/Group/UpdateGroup`;

      const res = await axios.put(url, payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      if(res.data.resCode === 222){
        alert(res.data.resMessage);
        await showAllGroups();
        setEditGroup(null);
      } else {
        alert(res.data.resMessage);
      }

    } catch (err) {
      console.error("Error renaming group:", err);
      alert("Failed to rename group.");
    }
  };

  const handleDeleteGroup = async (groupId: string) => {
    if (!window.confirm("Are you sure you want to delete this group?")) return;

    try {
      const username = countryCode + msisdn;

      const url = `${process.env.REACT_APP_API_URL}/Group/DeleteGroup?msisdn=${username}&service=${process.env.REACT_APP_SERVICE}&interface=${process.env.REACT_APP_INTERFACES}&langId=${process.env.REACT_APP_LANGID}&groupId=${groupId}&subType=${subType}`;

      const res = await axios.delete(url);
      
      if (res?.data?.resCode === 221) {
        alert(res?.data?.resMessage);
      } else {
        alert(res?.data?.resMessage);
      }

      await showAllGroups();
    } catch (error) {
      console.error("Error deleting group:", error);
      alert("Failed to delete group.");
    }
  };

  const getCountries = async () => {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/Cache/GetCountries?service=${process.env.REACT_APP_SERVICE}&interface=${process.env.REACT_APP_INTERFACES}&langId=${process.env.REACT_APP_LANGID}`
    );

    setCountryList(res?.data?.countryList || []);
    setSelectedCountry(res?.data?.countryList?.[0]?.countryCode || "");
  };

  const showAllGroups = async () => {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/Group/ShowAllGroups?msisdn=${
        countryCode + msisdn
      }&service=${process.env.REACT_APP_SERVICE}&interface=${
        process.env.REACT_APP_INTERFACES
      }&langId=${process.env.REACT_APP_LANGID}`
    );

    setGroups(res?.data?.crbtProfile?.groupList);
  };

  const getAllFriends = async () => {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/Friend/ShowFriends?msisdn=${
        countryCode + msisdn
      }&service=${process.env.REACT_APP_SERVICE}&interface=${
        process.env.REACT_APP_INTERFACES
      }&langId=${process.env.REACT_APP_LANGID}`
    );

    setFriends(res?.data?.crbtProfile?.friendList);
  };

  useEffect(() => {
    showAllGroups();
    getAllFriends();
    getCountries();
  }, []);

  return (
    <Box sx={{ p: 3 }}>
      {/* Alert */}
      {/* <Alert severity="success" sx={{ mb: 3, textAlign: "center" }}>
        <strong>Success!</strong> Action completed successfully.
      </Alert> */}

      <Typography variant="h4" align="center" sx={{ mb: 3 }}>
        Contacts
      </Typography>

      <Grid container spacing={4}>
        {/* CONTACTS SECTION */}
        <Grid size={6}>
          <Box textAlign="center">
            <Typography
              variant="h6"
              color="#000"
              display="flex"
              alignItems="center"
              justifyContent="center"
              gap={1}
            >
              <PersonIcon /> <b>Contacts</b>
            </Typography>
            <Divider sx={{ my: 2 }} />

            {friends.length > 0 ? (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Mobile</TableCell>
                    <TableCell align="center">Edit</TableCell>
                    <TableCell align="center">Delete</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {friends.map((friend) => (
                    <TableRow key={friend.fmsisdn}>
                      <TableCell>{friend.friendName}</TableCell>
                      <TableCell>{friend.fmsisdn}</TableCell>
                      <TableCell align="center">
                        <IconButton
                          color="primary"
                          onClick={() => setEditFriend({ ...friend })}
                        >
                          <EditIcon />
                        </IconButton>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteFriend(friend.fmsisdn)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Box>
                <Typography>No Contacts Found</Typography>
              </Box>
            )}

            <Button
              startIcon={<AddIcon />}
              variant="contained"
              sx={{ mt: 2, textTransform: "capitalize", fontWeight: "bold" }}
              onClick={() => setOpenFriendDialog(true)}
            >
              New Friend
            </Button>
          </Box>
        </Grid>

        {/* GROUPS SECTION */}
        <Grid size={6}>
          <Box textAlign="center">
            <Typography
              variant="h6"
              color="#000"
              display="flex"
              alignItems="center"
              justifyContent="center"
              gap={1}
            >
              <GroupIcon /> <b>Groups</b>
            </Typography>
            <Divider sx={{ my: 2 }} />

            { groups.length > 0 ? groups.map((group, idx) => (
              <Accordion key={group.groupId}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>{group.groupName}</Typography>
                  <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditGroup({ ...group });
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteGroup(group.groupId);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {group.friendList.length > 0 ? (
                    <Table size="small">
                      <TableBody>
                        {group.friendList.map((f) => (
                          <TableRow key={f.fmsisdn}>
                            <TableCell>{f.friendName}</TableCell>
                            <TableCell>{f.fmsisdn}</TableCell>
                            <TableCell align="right">
                              <IconButton
                                color="error"
                                size="small"
                                onClick={() =>
                                  handleRemoveFriendFromGroup(
                                    group.groupId,
                                    f.fmsisdn,
                                    f.friendName,
                                    group.groupName
                                  )
                                }
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <Typography color="text.secondary" sx={{ py: 1 }}>
                      No friends exist in {group.groupName}
                    </Typography>
                  )}
                  <Button
                    startIcon={<AddIcon />}
                    size="small"
                    variant="outlined"
                    sx={{ mt: 1 }}
                    onClick={() => {
                      setSelectedGroupForFriends(group);
                      setSelectedFriends(
                        group.friendList.map((f) => f.fmsisdn)
                      ); // pre-select existing ones
                      setOpenAddFriendToGroupDialog(true);
                    }}
                  >
                    Add New Friend
                  </Button>
                </AccordionDetails>
              </Accordion> 
            )) : <Box><Typography>No Groups Found</Typography></Box>}

            <Button
              startIcon={<AddIcon />}
              variant="contained"
              sx={{ mt: 2, textTransform: "capitalize", fontWeight: "bold" }}
              onClick={() => setOpenGroupDialog(true)}
            >
              New Group
            </Button>
          </Box>
        </Grid>
      </Grid>

      {/* Add Friend Modal */}
      {/* Add Friend Modal */}
      <Dialog
        open={openFriendDialog}
        onClose={() => setOpenFriendDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: 550,
          },
        }}
      >
        <DialogTitle>Friend</DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={3} alignItems="center">
            {/* Country */}
            <Grid size={3}>
              <Typography fontWeight={500}>Country:</Typography>
            </Grid>
            <Grid size={9}>
              <Select
                fullWidth
                size="small"
                value={selectedCountry}
                onChange={(e) => {
                  setSelectedCountry(e.target.value);

                  // update prefix of MSISDN when country changes
                  setNewFriend({
                    ...newFriend,
                    fmsisdn: e.target.value + newFriend.fmsisdn.substring(3),
                  });
                }}
              >
                {countryList.map((c) => (
                  <MenuItem key={c.countryCode} value={c.countryCode}>
                    {c.countryName} ({c.countryCode})
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            <Grid size={3}>
              <Typography fontWeight={500}>Mobile:</Typography>
            </Grid>
            <Grid size={9}>
              <Box sx={{ display: "flex", gap: 1 }}>
                <TextField
                  disabled
                  size="small"
                  sx={{ width: 80 }}
                  value={selectedCountry}
                />

                <TextField
                  size="small"
                  fullWidth
                  value={newFriend.fmsisdn.substring(prefixLength)}
                  onChange={(e) =>
                    setNewFriend({
                      ...newFriend,
                      fmsisdn: selectedCountry + e.target.value,
                    })
                  }
                />
              </Box>
            </Grid>

            {/* Name */}
            <Grid size={3}>
              <Typography fontWeight={500}>Name:</Typography>
            </Grid>
            <Grid size={9}>
              <TextField
                fullWidth
                size="small"
                value={newFriend.friendName}
                onChange={(e) =>
                  setNewFriend({ ...newFriend, friendName: e.target.value })
                }
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained"
            onClick={handleAddFriend}
            sx={{
              backgroundColor: "#1976d2",
              textTransform: "none",
              px: 3,
            }}
          >
            Submit
          </Button>

          <Button
            variant="contained"
            color="error"
            sx={{ textTransform: "none", px: 3 }}
            onClick={() => setOpenFriendDialog(false)}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Friend Modal */}
      <Dialog open={!!editFriend} onClose={() => setEditFriend(null)}>
        <DialogTitle>Edit Friend</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} alignItems="center">
            {/* Friend Name */}
            <Grid size={3}>
              <Typography fontWeight={500} width="130px" mr={2}>
                Friend Name:
              </Typography>
            </Grid>
            <Grid size={9}>
              <TextField
                fullWidth
                size="small"
                value={editFriend?.friendName || ""}
                onChange={(e) =>
                  setEditFriend((prev) =>
                    prev ? { ...prev, friendName: e.target.value } : null
                  )
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained"
            onClick={handleEditFriend}
            sx={{
              backgroundColor: "#1976d2",
              textTransform: "none",
              px: 3,
            }}
          >
            Submit
          </Button>

          <Button
            variant="contained"
            color="error"
            sx={{ textTransform: "none", px: 3 }}
            onClick={() => setEditFriend(null)}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openGroupDialog}
        onClose={() => setOpenGroupDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: 550,
          },
        }}
      >
        <DialogContent sx={{ p: 3 }}>
          <Grid container alignItems="center" spacing={2}>
            <Grid size={4}>
              <Typography fontWeight={500}>Group Name:</Typography>
            </Grid>

            <Grid size={8}>
              <TextField
                fullWidth
                size="small"
                value={newGroup}
                onChange={(e) => setNewGroup(e.target.value)}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button
              variant="contained"
              onClick={handleAddGroup}
              sx={{
                backgroundColor: "#1976d2",
                textTransform: "none",
                px: 3,
              }}
            >
              Submit
            </Button>

            <Button
              variant="contained"
              color="error"
              sx={{ textTransform: "none", px: 3 }}
              onClick={() => setOpenGroupDialog(false)}
            >
              Close
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Edit Group Modal */}
      <Dialog
        open={!!editGroup}
        onClose={() => setEditGroup(null)}
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: 550,
          },
        }}
      >
        <DialogTitle>Group</DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={3} alignItems="center">
            {/* Group Name */}
            <Grid size={3}>
              <Typography fontWeight={500}>Group Name:</Typography>
            </Grid>

            <Grid size={9}>
              <TextField
                fullWidth
                size="small"
                value={editGroup?.groupName || ""}
                onChange={(e) =>
                  setEditGroup((prev) =>
                    prev ? { ...prev, groupName: e.target.value } : null
                  )
                }
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained"
            onClick={handleEditGroup}
            sx={{
              backgroundColor: "#1976d2",
              textTransform: "none",
              px: 3,
            }}
          >
            Submit
          </Button>

          <Button
            variant="contained"
            color="error"
            sx={{ textTransform: "none", px: 3 }}
            onClick={() => setEditGroup(null)}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openAddFriendToGroupDialog}
        onClose={() => setOpenAddFriendToGroupDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: 550,
          },
        }}
      >
        <DialogTitle>Friend</DialogTitle>

        <DialogContent>
          <Typography sx={{ mb: 2, fontSize: 14 }}>
            Please select the friends to add to the group. Only friends in your
            friend list can be added. If no friends are in your list, please add
            them first.
          </Typography>

          <Divider sx={{ mb: 2 }} />

          <Table size="small">
            <TableBody>
              {friends.map((f) => (
                <TableRow key={f.fmsisdn}>
                  <TableCell>{f.friendName}</TableCell>
                  <TableCell>{f.fmsisdn}</TableCell>

                  {/* Checkbox for selection */}
                  <TableCell align="right">
                    <Checkbox
                      checked={selectedFriends.includes(f.fmsisdn)}
                      onChange={() => {
                        if (selectedFriends.includes(f.fmsisdn)) {
                          setSelectedFriends(
                            selectedFriends.filter((id) => id !== f.fmsisdn)
                          );
                        } else {
                          setSelectedFriends([...selectedFriends, f.fmsisdn]);
                        }
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>

        <DialogActions>
          <Button variant="contained" onClick={handleAddFriendToGroup}>
            Submit
          </Button>

          <Button
            variant="contained"
            color="error"
            onClick={() => setOpenAddFriendToGroupDialog(false)}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContactsPage;
